import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import PaymentCheckout from '../paymentAdapters/PaymentCheckout';
import PaymentOptionsModal, { providerDisplayName } from './PaymentOptionsModal';
import { startInvoicePayment } from '../../services/invoiceService';
import { getPaymentOptions, verifyPayment } from '../../services/paymentService';
import { startRenewalPayment } from '../../services/portalRenewalService';

const money = (value) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(Number(value || 0));

class CustomerPaymentModal extends Component {
  state = {
    loading: true,
    payment: null,
    paymentOptions: [],
    selectedProvider: '',
    useCredit: false,
    creditAmount: '',
    availableCredit: 0,
    submitting: false,
    verifying: false,
    paid: false,
    warning: null,
  };

  componentDidMount() { this.loadOptions(); }

  amountDue = () => Number(this.props.invoice?.balance || this.props.amount || this.props.payment?.total_due || this.props.payment?.amount || 0);
  creditLimit = () => Math.min(Number(this.state.availableCredit || 0), this.amountDue());

  loadOptions = async () => {
    try {
      const response = await getPaymentOptions();
      const options = response.data?.data || response.data || [];
      const defaultProvider = response.data?.defaultProvider || options.find((o) => o.isDefault)?.providerKey || '';
      const availableCredit = Number(response.data?.credit?.amount || this.props.availableCredit || 0);
      this.setState({
        paymentOptions: options,
        selectedProvider: this.props.payment?.providerKey || defaultProvider || options[0]?.providerKey || '',
        availableCredit,
        loading: false,
      });
    } catch (ex) {
      toast.error(ex.response?.data?.message || 'Could not prepare payment.');
      this.props.onClose();
    }
  };

  submit = async () => {
    const creditAmount = this.state.useCredit ? Math.min(Number(this.state.creditAmount) || 0, this.creditLimit()) : 0;
    if (this.state.useCredit && !(creditAmount > 0)) { toast.error('Enter an Available Credit amount greater than zero.'); return; }
    this.setState({ submitting: true, warning: null });
    try {
      // Every explicit Continue is a new payment attempt: the request carries
      // no reused checkout identity, so the boundary always creates a fresh
      // Transaction with a fresh 12-digit transid and provider attempt.
      const response = this.props.invoice
        ? await startInvoicePayment(this.props.invoice.id, { providerKey: this.state.selectedProvider, creditAmount })
        : await startRenewalPayment(this.props.accountId, { quantity: this.props.quantity, creditAmount, providerKey: this.state.selectedProvider });
      const payment = response.data?.data || response.data;
      if (payment.settled || payment.settledByCredit) { toast.success('Payment completed with Available Credit.'); this.props.onPaid(); return; }
      this.setState({ payment, submitting: false });
    } catch (ex) {
      const message = ex.response?.data?.message || ex.response?.data || 'Could not start payment.';
      const status = ex.response?.status;
      toast.error(message);
      this.setState({ submitting: false });
    }
  };

  verify = async (reference) => {
    const payment = this.state.payment;
    if (!payment && !reference) return;
    try {
      this.setState({ verifying: true });
      const response = await verifyPayment({ reference: reference || payment.transid, providerKey: payment.providerKey });
      if (response.data?.verified || response.data?.settled || response.data?.settledByCredit) {
        toast.success(response.data?.message || 'Payment successful.');
        // Mark the flow paid before unwinding so a trailing provider onclose
        // (e.g. Flutterwave) cannot reopen or unwind the completed flow.
        this.setState({ paid: true });
        this.props.onPaid();
        return;
      }
      if (response.data?.status === 'failed') {
        toast.error(response.data?.message || 'Could not verify payment.');
        return;
      }
      // Callback accepted; close the modal and let the parent refresh the record.
      this.setState({ paid: true });
      this.props.onPaid();
    } catch (ex) { toast.error(ex.response?.data?.message || 'Payment verification failed.'); }
    finally { this.setState({ verifying: false }); }
  };

  // Provider checkout dismissal is a full unwind to the originating screen —
  // never a return to a stale provider-selection state. While a success
  // verification is in flight or completed, a trailing provider close event
  // is ignored so it cannot unwind the paid flow.
  handleProviderClose = () => {
    if (this.state.verifying || this.state.paid) return;
    this.setState({ payment: null });
    this.props.onClose();
  };

  // Once a payment attempt exists the selection modal is not rendered at all;
  // the provider checkout phase stands alone so provider overlays (Squad,
  // Interswitch) never stack on top of — or flash through — the selection
  // chrome. The explicit Cancel control is required for providers whose
  // checkout exposes no cancel callback (interswitch inline webpayCheckout).
  renderCheckout() {
    const { payment, verifying, submitting } = this.state;
    const busy = verifying || submitting;
    const providerName = providerDisplayName({ providerKey: payment.providerKey });
    return (
      <>
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-label="Checkout in progress">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content shadow">
              <div className="modal-header">
                <h5 className="modal-title">Checkout in progress</h5>
                <button type="button" className="close" aria-label="Close" onClick={this.handleProviderClose} disabled={busy}>
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                {verifying ? (
                  <p className="d-flex align-items-center mb-3" role="status" aria-live="polite">
                    <span className="spinner-border spinner-border-sm mr-2" aria-hidden="true" />
                    Finalizing your payment… please wait.
                  </p>
                ) : (
                  <p className="text-muted mb-3">
                    Complete your payment of <strong>{money(payment.total_due || payment.amount)}</strong> with {providerName} in the provider checkout window.
                  </p>
                )}
                <div className={verifying ? 'd-none' : undefined}>
                  <PaymentCheckout
                    payment={payment}
                    onPaid={this.verify}
                    onClose={this.handleProviderClose}
                    autoOpen
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={this.handleProviderClose}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" onClick={busy ? undefined : this.handleProviderClose} role="presentation" />
      </>
    );
  }

  render() {
    const { invoice, title, accountId, subscriptionLabel } = this.props;
    const { loading, payment, paymentOptions, selectedProvider, useCredit, creditAmount, availableCredit, submitting, verifying } = this.state;
    if (payment) return this.renderCheckout();
    return (
      <PaymentOptionsModal
        title={title || (invoice ? `Pay invoice ${invoice.invoicenum}` : 'Pay subscription')}
        loading={loading}
        amountDue={this.amountDue()}
        payment={null}
        paymentOptions={paymentOptions}
        selectedProvider={selectedProvider}
        onSelectProvider={(providerKey) => this.setState({ selectedProvider: providerKey })}
        availableCredit={availableCredit}
        useCredit={useCredit}
        creditAmount={creditAmount}
        creditLimit={this.creditLimit()}
        onUseCreditChange={(event) => this.setState({ useCredit: event.target.checked, creditAmount: event.target.checked ? String(this.creditLimit()) : '' })}
        onCreditAmountChange={(event) => this.setState({ creditAmount: event.target.value })}
        onSubmit={this.submit}
        onClose={this.props.onClose}
        busy={submitting || verifying}
        submitting={submitting}
        description={subscriptionLabel || (accountId ? 'Subscription renewal' : undefined)}
      />
    );
  }
}

CustomerPaymentModal.propTypes = {
  invoice: PropTypes.object,
  payment: PropTypes.object,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  quantity: PropTypes.number,
  amount: PropTypes.number,
  availableCredit: PropTypes.number,
  title: PropTypes.string,
  subscriptionLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onPaid: PropTypes.func.isRequired,
};

export default CustomerPaymentModal;
